import { useFormContext, useWatch } from "react-hook-form";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Card from "ds/components/Card";
import FeedbackActions from "ds/components/Feedback/Actions";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import { BlueprintInput } from "types/generated";

import { buildValidationRules } from "./helpers";
import { SecretFieldAction, SecretFieldsState, VersionTemplateFormFields } from "./types";

type DeploymentFormSecretFieldProps = {
  input: BlueprintInput;
  editMode?: boolean;
  secretFieldsState?: SecretFieldsState;
};

const DeploymentFormSecretField = ({
  input,
  editMode,
  secretFieldsState,
}: DeploymentFormSecretFieldProps) => {
  const { register, control, formState } = useFormContext<VersionTemplateFormFields>();
  const validationRules = buildValidationRules(input.validations);
  const isOptional = !input.validations?.required;
  const error = formState.errors.inputs?.[input.id];

  const action: SecretFieldAction = secretFieldsState?.fieldActions.get(input.id) ?? SecretFieldAction.KEEP;
  const setAction = secretFieldsState?.setAction;
  // Only show "configured" state if in edit mode AND this secret exists in the deployment
  const hasExistingSecret = editMode && secretFieldsState?.existingSecretIds.has(input.id);

  // Watch the current input value to show contextual warnings in "change" mode
  const currentValue = useWatch({ control, name: `inputs.${input.id}` }) as string;

  // Create mode: show a normal secret input
  if (!hasExistingSecret) {
    return (
      <FormField
        key={input.id}
        label={input.name}
        helperText={input.description}
        noMargin
        isOptional={isOptional}
        error={error?.message}
      >
        {({ ariaInputProps }) => (
          <SecretInput
            {...register(`inputs.${input.id}`, validationRules)}
            {...ariaInputProps}
            placeholder="Enter secret value"
            error={!!error}
          />
        )}
      </FormField>
    );
  }

  // Edit mode — KEEP (default): secret is configured, no changes pending.
  // Card wrapping a disabled masked input + info Banner + action buttons below.
  if (action === SecretFieldAction.KEEP) {
    const isRequired = input.validations?.required;

    return (
      <Card>
        <Box direction="column" gap="large">
          <FormField label={input.name} noMargin>
            {() => (
              <Input
                value="••••••••••••••••••"
                disabled
                aria-label={`${input.name} (secret, configured)`}
              />
            )}
          </FormField>
          <Banner variant="info">
            A secret value is configured for this field. It cannot be displayed for security
            reasons.
            <FeedbackActions>
              <Button
                variant="secondary"
                size="small"
                onPress={() => setAction?.(input.id, SecretFieldAction.CHANGE)}
              >
                Change secret
              </Button>
              {!isRequired && (
                <Button
                  variant="dangerSecondary"
                  size="small"
                  onPress={() => setAction?.(input.id, SecretFieldAction.DELETE)}
                >
                  Remove secret
                </Button>
              )}
            </FeedbackActions>
          </Banner>
        </Box>
      </Card>
    );
  }

  // Edit mode — CHANGE: user is providing a new value.
  // Wrapped in a Card with input + warning Banner (with action button inside).
  // When the input is blank, warn that deploying will replace the secret with an empty value.
  if (action === SecretFieldAction.CHANGE) {
    const isBlank = !currentValue || (typeof currentValue === "string" && !currentValue.trim());

    return (
      <Card>
        <Box direction="column" gap="medium">
          <FormField label={input.name} noMargin isOptional={isOptional} error={error?.message}>
            {({ ariaInputProps }) => (
              <SecretInput
                {...register(`inputs.${input.id}`, validationRules)}
                {...ariaInputProps}
                placeholder="Enter new secret value or leave blank"
                error={!!error}
              />
            )}
          </FormField>
          <Banner variant="warning">
            {isBlank
              ? "The field is currently blank. Deploying will replace the existing secret with an empty value."
              : "A new value will replace the existing secret. Deploying will overwrite the current value."}
            <FeedbackActions>
              <Button
                variant="secondary"
                size="small"
                onPress={() => setAction?.(input.id, SecretFieldAction.KEEP)}
              >
                Keep existing secret
              </Button>
            </FeedbackActions>
          </Banner>
        </Box>
      </Card>
    );
  }

  // Edit mode — RESET: set value to empty string (field still exists, but blank).
  // Card with empty disabled input (previews the outcome) + warning Banner (with action button inside).
  if (action === SecretFieldAction.RESET) {
    return (
      <Card>
        <Box direction="column" gap="medium">
          <FormField label={input.name} noMargin>
            {() => (
              <Input value="" disabled aria-label={`${input.name} (will be reset to blank)`} />
            )}
          </FormField>
          <Banner variant="warning">
            This secret will be reset to blank when you deploy. The field will still exist but with
            no value.
            <FeedbackActions>
              <Button
                variant="secondary"
                size="small"
                onPress={() => setAction?.(input.id, SecretFieldAction.KEEP)}
              >
                Keep existing secret
              </Button>
            </FeedbackActions>
          </Banner>
        </Box>
      </Card>
    );
  }

  // Edit mode — DELETE: remove the field entirely (it will no longer exist).
  // Standalone danger Banner with title and action button inside — no Card wrapping.
  return (
    <Banner variant="danger" title={input.name}>
      This secret will be completely removed when you deploy. The field will no longer exist on this
      deployment.
      <FeedbackActions>
        <Button variant="secondary" size="small" onPress={() => setAction?.(input.id, SecretFieldAction.KEEP)}>
          Keep existing secret
        </Button>
      </FeedbackActions>
    </Banner>
  );
};

export default DeploymentFormSecretField;
